type SessionStatusLike = {
  status?: string | null;
  rescheduled_session_id?: string | null;
};

type BillingSessionLike =
  | {
      status?: string | null;
    }
  | null
  | undefined;

export class SessionStatus {
  static readonly SCHEDULED = "scheduled";
  static readonly COMPLETED = "completed";
  static readonly INSTRUCTOR_DELETED = "instructor-deleted";
  static readonly STUDENT_DELETED = "student-deleted";
  static readonly ADMIN_DELETED = "admin-deleted";
  static readonly INSTRUCTOR_CANCELED = "instructor-canceled";
  static readonly STUDENT_CANCELED = "student-canceled";
  static readonly ISSUES_RESCHEDULED = "issues-rescheduled";
  static readonly INSTRUCTOR_MISSED = "instructor-missed";
  static readonly STUDENT_MISSED = "student-missed";
  static readonly IN_PROGRESS = "in-progress";
}

export class StudentBillingType {
  static readonly CHARGED = "charged";
  static readonly NOT_CHARGED = "not-charged";
}

export class InstructorBillingType {
  static readonly PAID = "paid";
  static readonly NOT_PAID = "not-paid";
}

export class SessionStatusDisplayName {
  static readonly SCHEDULED = "Scheduled";
  static readonly COMPLETED = "Completed";
  static readonly INSTRUCTOR_DELETED = "Instructor Deleted";
  static readonly STUDENT_DELETED = "Student Deleted";
  static readonly ADMIN_DELETED = "Admin Deleted";
  static readonly INSTRUCTOR_CANCELED = "Instructor Cancelled within 24 hours";
  static readonly STUDENT_CANCELED = "Student Cancelled within 24 hours";
  static readonly INSTRUCTOR_RESCHEDULED = "Instructor Rescheduled within 24 hours";
  static readonly STUDENT_RESCHEDULED = "Student Rescheduled within 24 hours";
  static readonly ISSUES_RESCHEDULED = "Technical Difficulties";
  static readonly INSTRUCTOR_MISSED = "Instructor Missed";
  static readonly STUDENT_MISSED = "Student Missed";
  static readonly IN_PROGRESS = "In Progress";
}

export function getStatusDisplayName(session: SessionStatusLike) {
  switch (session.status) {
    case SessionStatus.SCHEDULED:
      return SessionStatusDisplayName.SCHEDULED;
    case SessionStatus.COMPLETED:
      return SessionStatusDisplayName.COMPLETED;
    case SessionStatus.INSTRUCTOR_DELETED:
      return SessionStatusDisplayName.INSTRUCTOR_DELETED;
    case SessionStatus.STUDENT_DELETED:
      return SessionStatusDisplayName.STUDENT_DELETED;
    case SessionStatus.ADMIN_DELETED:
      return SessionStatusDisplayName.ADMIN_DELETED;
    case SessionStatus.INSTRUCTOR_CANCELED:
      if (isInstructorRescheduled(session)) {
        return SessionStatusDisplayName.INSTRUCTOR_RESCHEDULED;
      }
      return SessionStatusDisplayName.INSTRUCTOR_CANCELED;
    case SessionStatus.STUDENT_CANCELED:
      if (isStudentRescheduled(session)) {
        return SessionStatusDisplayName.STUDENT_RESCHEDULED;
      }
      return SessionStatusDisplayName.STUDENT_CANCELED;
    case SessionStatus.ISSUES_RESCHEDULED:
      return SessionStatusDisplayName.ISSUES_RESCHEDULED;
    case SessionStatus.INSTRUCTOR_MISSED:
      return SessionStatusDisplayName.INSTRUCTOR_MISSED;
    case SessionStatus.STUDENT_MISSED:
      return SessionStatusDisplayName.STUDENT_MISSED;
    case SessionStatus.IN_PROGRESS:
      return SessionStatusDisplayName.IN_PROGRESS;
    default:
      return session.status;
  }
}

export function isScheduled(status?: string | null) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.SCHEDULED;
}

export function isCompleted(status?: string | null) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.COMPLETED;
}

export function isDeleted(status?: string | null) {
  if (!status) {
    return false;
  }
  return (
    status === SessionStatus.INSTRUCTOR_DELETED ||
    status === SessionStatus.STUDENT_DELETED ||
    status === SessionStatus.ADMIN_DELETED
  );
}

export function isCanceled(status?: string | null) {
  if (!status) {
    return false;
  }
  return (
    status === SessionStatus.INSTRUCTOR_CANCELED ||
    status === SessionStatus.STUDENT_CANCELED
  );
}

export function isIssuesRescheduled(status?: string | null) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.ISSUES_RESCHEDULED;
}

export function isInstructorCanceled(status?: string | null) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.INSTRUCTOR_CANCELED;
}

export function isInstructorRescheduled(session?: SessionStatusLike | null) {
  if (!session) {
    return false;
  }
  return isInstructorCanceled(session.status) && !!session.rescheduled_session_id;
}

export function isInstructorMissed(status?: string | null) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.INSTRUCTOR_MISSED;
}

export function isStudentCanceled(status?: string | null) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.STUDENT_CANCELED;
}

export function isStudentRescheduled(session?: SessionStatusLike | null) {
  if (!session) {
    return false;
  }
  return isStudentCanceled(session.status) && !!session.rescheduled_session_id;
}

export function isStudentMissed(status?: string | null) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.STUDENT_MISSED;
}

export function isInProgress(status?: string | null) {
  if (!status) {
    return false;
  }
  return status === SessionStatus.IN_PROGRESS;
}

export function getAllStatuses() {
  return [
    SessionStatus.SCHEDULED,
    SessionStatus.COMPLETED,
    SessionStatus.INSTRUCTOR_DELETED,
    SessionStatus.STUDENT_DELETED,
    SessionStatus.ADMIN_DELETED,
    SessionStatus.INSTRUCTOR_CANCELED,
    SessionStatus.STUDENT_CANCELED,
    SessionStatus.ISSUES_RESCHEDULED,
    SessionStatus.INSTRUCTOR_MISSED,
    SessionStatus.STUDENT_MISSED,
    SessionStatus.IN_PROGRESS,
  ];
}

export function isLessonFinishedOrInProgress(status?: string | null) {
  if (!status) {
    return false;
  }
  return (
    status === SessionStatus.COMPLETED ||
    status === SessionStatus.IN_PROGRESS ||
    status === SessionStatus.ISSUES_RESCHEDULED ||
    status === SessionStatus.INSTRUCTOR_MISSED ||
    status === SessionStatus.STUDENT_MISSED
  );
}

export function isLessonStatusWithReason(status?: string | null) {
  if (!status) {
    return false;
  }
  return (
    status === SessionStatus.STUDENT_MISSED || status === SessionStatus.STUDENT_CANCELED
  );
}

export function getStudentBillingType(session: BillingSessionLike) {
  if (!session) {
    return null;
  }
  const status = session.status;
  if (
    isScheduled(status) ||
    isCompleted(status) ||
    isStudentMissed(status) ||
    isStudentCanceled(status) ||
    isInProgress(status)
  ) {
    return StudentBillingType.CHARGED;
  } else {
    return StudentBillingType.NOT_CHARGED;
  }
}

export function getInstructorBillingType(session: BillingSessionLike) {
  if (!session) {
    return null;
  }
  const status = session.status;
  if (isInstructorMissed(status) || isInstructorCanceled(status) || isDeleted(status)) {
    return InstructorBillingType.NOT_PAID;
  } else {
    return InstructorBillingType.PAID;
  }
}
